import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import * as menuApi from '@/services/endpoints/menu'
import type { FlatMenuEntry, MenuItem } from '@/types/menu'
import { useAuthStore } from './auth'

/**
 * Canonical form used to compare a route path with a menu path: leading slash,
 * no trailing slash, lower-cased. Menu paths are authored lower-case, and this
 * keeps `/Incomings/` and `/incomings` from being treated as different pages.
 */
export function normalizeMenuPath(path: string): string {
  const trimmed = path.trim()
  if (!trimmed) return '/'
  const withLeadingSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`
  const withoutTrailingSlash =
    withLeadingSlash.length > 1 ? withLeadingSlash.replace(/\/+$/, '') : withLeadingSlash
  return withoutTrailingSlash.toLowerCase()
}

/**
 * Same as `normalizeMenuPath` but for a menu entry's `path`, which is `null`
 * for categories and for pages that have not been wired up yet. Returns `null`
 * when there is nothing navigable, so callers can skip the entry instead of
 * inventing a route for it.
 */
export function normalizeItemPath(path: string | null | undefined): string | null {
  if (typeof path !== 'string' || path.trim().length === 0) return null
  return normalizeMenuPath(path)
}

/** Depth-first walk keeping the labels of the ancestors for breadcrumbs. */
function flattenTree(items: MenuItem[], trail: string[] = []): FlatMenuEntry[] {
  return items.flatMap((item) => [
    { item, trail },
    ...flattenTree(item.children ?? [], [...trail, item.label]),
  ])
}

/**
 * Applies the `logged` rule: an entry flagged `logged` must not be shown to an
 * anonymous visitor. The backend already filters by role/subscription, so this
 * is the one visibility rule the client owns.
 */
function filterForVisibility(
  items: MenuItem[],
  isAuthenticated: boolean,
  localCounts: Record<string, number>,
): MenuItem[] {
  return items
    .filter((item) => isAuthenticated || !item.logged)
    .map((item) => {
      const path = normalizeItemPath(item.path)
      const local = path === null ? undefined : localCounts[path]
      return {
        ...item,
        pending_count: local ?? item.pending_count,
        children: filterForVisibility(item.children ?? [], isAuthenticated, localCounts),
      }
    })
}

export const useMenuStore = defineStore('menu', () => {
  const auth = useAuthStore()

  /** Raw tree exactly as returned by the API. */
  const items = ref<MenuItem[]>([])
  const loading = ref(false)
  const loaded = ref(false)
  /**
   * Badge counts polled by the client, by normalised path. They override the
   * `pending_count` of `GET /menu`, which is cached per user server side and
   * only reloaded on login or group switch: a count that changes on its own
   * (attacks landing) would otherwise stay stale for the whole session. Kept
   * apart from `items` so a menu reload doesn't wipe them.
   */
  const localCounts = ref<Record<string, number>>({})

  /** What the sidebar renders. */
  const visibleItems = computed<MenuItem[]>(() =>
    filterForVisibility(items.value, auth.isAuthenticated, localCounts.value),
  )

  const flatVisibleEntries = computed<FlatMenuEntry[]>(() => flattenTree(visibleItems.value))

  /** Collects the navigable paths of a flattened tree, skipping category entries. */
  function collectPaths(entries: FlatMenuEntry[]): Set<string> {
    const paths = new Set<string>()
    for (const entry of entries) {
      const path = normalizeItemPath(entry.item.path)
      if (path !== null) paths.add(path)
    }
    return paths
  }

  /** Paths the current user is allowed to open. */
  const allowedPaths = computed<Set<string>>(() => collectPaths(flatVisibleEntries.value))

  /**
   * Every path the API mentioned, including entries hidden because the visitor
   * is anonymous. Lets the router tell "needs login" apart from "does not exist".
   */
  const knownPaths = computed<Set<string>>(() => collectPaths(flattenTree(items.value)))

  function findByPath(path: string): MenuItem | null {
    const target = normalizeMenuPath(path)
    return (
      flattenTree(items.value).find((entry) => normalizeItemPath(entry.item.path) === target)
        ?.item ?? null
    )
  }

  function isAllowed(path: string): boolean {
    return allowedPaths.value.has(normalizeMenuPath(path))
  }

  function isKnown(path: string): boolean {
    return knownPaths.value.has(normalizeMenuPath(path))
  }

  /**
   * Loads the menu. Must be re-run on login, logout and every group switch,
   * because the backend personalises the tree per user and selected group.
   */
  async function fetch(force = false): Promise<MenuItem[]> {
    if (loaded.value && !force) return items.value
    loading.value = true
    try {
      items.value = await menuApi.getMenu()
      loaded.value = true
      return items.value
    } catch (error) {
      // A failed menu load must not brick navigation: keep whatever we had.
      loaded.value = true
      throw error
    } finally {
      loading.value = false
    }
  }

  /** Sets (or with `null` drops) the client-side badge count of a path. */
  function setLocalCount(path: string, count: number | null): void {
    const key = normalizeMenuPath(path)
    const next = { ...localCounts.value }
    if (count === null) delete next[key]
    else next[key] = count
    localCounts.value = next
  }

  function reload(): Promise<MenuItem[]> {
    return fetch(true)
  }

  function reset(): void {
    items.value = []
    localCounts.value = {}
    loaded.value = false
  }

  return {
    items,
    loading,
    loaded,
    visibleItems,
    flatVisibleEntries,
    allowedPaths,
    knownPaths,
    findByPath,
    isAllowed,
    isKnown,
    fetch,
    reload,
    reset,
    setLocalCount,
  }
})
